import { installedApps, text } from "@norn/widget-renderer/model"
import type { DefinitionSet } from "@/lib/defs"
import { exactText } from "@/lib/lossless"
import { asArray, asString, isRecord } from "@/lib/utils"
import type { Ontology } from "./ontology"

/**
 * Metric tiles read from installed board apps: a WidgetIR section whose list binds a materialized
 * metric type (one carrying `value` and `buckets`). The section label names the tile; the binding's
 * literal `eq` filter selects its row. Nothing here names a particular metric.
 */
export interface MetricTile {
  readonly id: string
  readonly appId: string
  readonly pageId: string
  readonly title: string
  readonly caption?: string
  readonly help?: string
  readonly objectType: string
  /** The selected row's key; null means every row (a series such as months). */
  readonly rowKey: string | null
  readonly sort?: string
  /** The value field's own label ("Reserved orders"), used when section titles collide. */
  readonly valueLabel?: string
}

function walk(node: unknown, visit: (node: Record<string, unknown>, parentLabel: string | undefined) => void, parentLabel?: string, locale = "en") {
  if (!isRecord(node)) return
  const component = isRecord(node.component) ? node.component : {}
  const section = component.kind === "section" && isRecord(component.section) ? component.section : undefined
  const label = section ? text(section.label as never, locale) || parentLabel : parentLabel
  visit(node, label)
  for (const child of asArray(node.children)) walk(child, visit, label, locale)
}

export function metricTiles(set: DefinitionSet, ontology: Ontology, locale: string): MetricTile[] {
  const byArtifact = new Map([...ontology.types.values()].map((t) => [t.artifactId, t]))
  const tiles: MetricTile[] = []
  for (const app of installedApps(set)) {
    const ux = app.ux as unknown as Record<string, unknown>
    const bindings = new Map(asArray(ux.dataBindings).filter(isRecord).map((b) => [asString(b.bindingId) ?? "", b]))
    const fields = asArray(ux.fields).filter(isRecord)
    for (const page of app.ux.pages) {
      walk(
        (page as unknown as Record<string, unknown>).root,
        (node, label) => {
          const component = isRecord(node.component) ? node.component : {}
          if (component.kind !== "list" || typeof component.dataBindingId !== "string") return
          const binding = bindings.get(component.dataBindingId)
          const object = binding && isRecord(binding.object) ? asString(binding.object.id) : undefined
          const type = object ? byArtifact.get(object) : undefined
          if (!type || !type.fields.some((f) => f.id === "value") || !type.fields.some((f) => f.id === "buckets")) return
          const query = isRecord(binding?.query) ? binding.query : {}
          const literal = asArray(query.filters)
            .filter(isRecord)
            .find((f) => f.operator === "eq" && isRecord(f.value) && f.value.kind === "literal" && typeof f.value.value === "string")
          const rowKey = literal && isRecord(literal.value) ? (literal.value.value as string) : null
          const [title, ...rest] = (label ?? type.pluralLabel).split(" · ")
          const valueField = fields.find((f) => isRecord(f.target) && isRecord(f.target.property) && f.target.property.bindingId === component.dataBindingId && f.target.property.propertyId === "value")
          const valueLabel = valueField && isRecord(valueField.label) ? text(valueField.label as never, locale) : undefined
          tiles.push({
            valueLabel,
            id: `${app.appId}/${page.pageId}/${component.dataBindingId}`,
            appId: app.appId,
            pageId: page.pageId,
            title: title ?? type.pluralLabel,
            caption: rest.join(" · ") || undefined,
            help: valueField && isRecord(valueField.help) ? text(valueField.help as never, locale) : undefined,
            objectType: type.name,
            rowKey,
          })
        },
        undefined,
        locale,
      )
    }
  }
  // Two tiles with the same section title ("Stock activity") are told apart by what they count.
  const titles = new Map<string, number>()
  for (const tile of tiles) titles.set(tile.title, (titles.get(tile.title) ?? 0) + 1)
  return tiles.map((tile) =>
    (titles.get(tile.title) ?? 0) > 1 && tile.valueLabel ? { ...tile, title: tile.valueLabel, caption: tile.title } : tile,
  )
}

export interface Bucket {
  readonly group: string | null
  /** For bar geometry only; labels use `text`. */
  readonly value: number
  /** Exact decimal text of the bucket value. */
  readonly text: string
}

export function bucketsOf(value: unknown): Bucket[] {
  return asArray(value).flatMap((b) => {
    if (!isRecord(b)) return []
    const text = typeof b.value === "number" ? String(b.value) : (exactText(b.value) ?? (typeof b.value === "string" ? b.value : undefined))
    const v = text === undefined ? Number.NaN : Number(text)
    return text !== undefined && Number.isFinite(v) ? [{ group: typeof b.group === "string" ? b.group : null, value: v, text }] : []
  })
}

/**
 * The record types a tile's groups may name: business types whose display name the board's own
 * copy uses ("Top customers · …" → Customer). Each candidate is checked against real authorized
 * reads; a group only shows a title when its key resolves to a readable record of that type.
 */
function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^$()|[\]\\{}]/g, "\\$&")
}

export function groupCandidates(tile: MetricTile, ontology: Ontology | undefined): string[] {
  if (!ontology) return []
  const copy = `${tile.title} ${tile.caption ?? ""} ${tile.help ?? ""}`.toLowerCase()
  return [...ontology.types.values()]
    .filter((ty) => ty.titleFields.length && ty.layer === "tenant" && new RegExp(String.raw`\b` + escapeRegExp(ty.label.toLowerCase())).test(copy))
    .map((ty) => ty.name)
    .slice(0, 3)
}

/** Record types a metric counts, named in its title or caption (never its help text), its own namespace first. */
export function drillTypes(tile: MetricTile, ontology: Ontology | undefined): string[] {
  if (!ontology) return []
  const copy = `${tile.title} ${tile.caption ?? ""}`.toLowerCase()
  const ns = tile.objectType.slice(0, tile.objectType.lastIndexOf(".") + 1)
  const seen = new Set<string>()
  return [...ontology.types.values()]
    .filter((ty) => ty.layer === "tenant" && new RegExp(String.raw`\b` + escapeRegExp(ty.label.toLowerCase())).test(copy))
    .sort((a, b) => Number(!a.name.startsWith(ns)) - Number(!b.name.startsWith(ns)))
    .filter((ty) => !seen.has(ty.label) && Boolean(seen.add(ty.label)))
    .map((ty) => ty.name)
    .slice(0, 3)
}
